// API Configuration
export const API_CONFIG = {
  // Use relative URL in development to go through Vite proxy (avoids CORS)
  // Use full URL in production
  baseURL: import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_BASE_URL || 'https://demo.aml-guard.com/services'),
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  auth: {
    // REST API Token Authentication
    token: import.meta.env.VITE_API_TOKEN || '',
    // Web UI and SOAP API credentials (fallback)
    username: import.meta.env.VITE_API_USERNAME || '',
    password: import.meta.env.VITE_API_PASSWORD || '',
  },
  user: {
    id: import.meta.env.VITE_USER_ID || '',
    firstName: import.meta.env.VITE_USER_FIRST_NAME || '',
    lastName: import.meta.env.VITE_USER_LAST_NAME || '',
    company: import.meta.env.VITE_USER_COMPANY || '',
  },
  // WhatsApp Authentication API
  whatsApp: {
    baseURL: import.meta.env.VITE_WHATSAPP_API_URL || 'https://whatsappclient.bigdataheaven-software.de',
    adminPhone: import.meta.env.VITE_WHATSAPP_ADMIN_PHONE || '+491778822990',
  },
}

// AML-Guard REST API Endpoints
// Documentation: https://demo.aml-guard.com/doc/d58e3582afa99040e27b92b13c8f2280/crif-api/
export const API_ENDPOINTS = {
  // Health Check
  health: {
    check: '/api/v1/health',
  },

  // Authentication
  auth: {
    login: '/api/v1/auth/login',
    logout: '/api/v1/auth/logout',
    refresh: '/api/v1/auth/refresh',
    register: '/api/v1/auth/register',
    profile: '/api/v1/auth/profile',
    // WhatsApp Cross-Device Authentication
    requestPin: '/api/v1/auth/request-pin',
    status: '/api/v1/auth/status/:authRequestId',
    magic: '/api/v1/auth/magic/:token',
    verifyPin: '/api/v1/auth/verify-pin',
  },

  // User Management
  users: {
    list: '/api/v1/users',
    detail: '/api/v1/users/:id',
    create: '/api/v1/users',
    update: '/api/v1/users/:id',
    delete: '/api/v1/users/:id',
    activate: '/api/v1/users/:id/activate',
    deactivate: '/api/v1/users/:id/deactivate',
    resetPassword: '/api/v1/users/:id/reset-password',
    bulkOperation: '/api/v1/users/bulk',
    stats: '/api/v1/users/stats',
    roles: '/api/v1/users/roles',
    permissionMatrix: '/api/v1/users/permissions/matrix',
    export: '/api/v1/users/export',
    auditLogs: '/api/v1/users/:id/audit-logs',
    search: '/api/v1/users/search',
  },

  // Archiving Management
  archiving: {
    list: '/api/v1/archivings',
    detail: '/api/v1/archivings/:archivingKey',
    export: '/api/v1/archivings/export',
  },

  // Batch Operations
  batch: {
    entities: '/api/v1/batch/entities',
    decisions: '/api/v1/batch/decisions',
    search: '/api/v1/batch/search',
  },

  // Data Pool Management
  dataPools: {
    list: '/api/v1/datapools',
    create: '/api/v1/datapools',
    detail: '/api/v1/datapools/:dataPoolKey',
    update: '/api/v1/datapools/:dataPoolKey',
    delete: '/api/v1/datapools/:dataPoolKey',
    stats: '/api/v1/datapools/:dataPoolKey/stats',
  },

  // Entity Management
  entities: {
    list: '/api/v1/datapools/:dataPoolKey/entities',
    create: '/api/v1/datapools/:dataPoolKey/entities',
    detail: '/api/v1/datapools/:dataPoolKey/entities/:amlId',
    update: '/api/v1/datapools/:dataPoolKey/entities/:amlId',
    delete: '/api/v1/datapools/:dataPoolKey/entities/:amlId',
    search: '/api/v1/datapools/:dataPoolKey/entities/search',
    import: '/api/v1/datapools/:dataPoolKey/entities/import',
    export: '/api/v1/datapools/:dataPoolKey/entities/export',
  },

  // File Management
  files: {
    upload: '/api/v1/file',
    download: '/api/v1/file/:fileKey',
    status: '/api/v1/file/:fileKey/status',
  },

  // Job Management
  jobs: {
    list: '/api/v1/jobs',
    create: '/api/v1/jobs',
    detail: '/api/v1/jobs/:jobKey',
    update: '/api/v1/jobs/:jobKey',
    delete: '/api/v1/jobs/:jobKey',
    pause: '/api/v1/jobs/:jobKey/pause',
    resume: '/api/v1/jobs/:jobKey/resume',
    run: '/api/v1/jobs/:jobKey/run',
    results: '/api/v1/jobs/:jobKey/results',
  },

  // Monitoring
  monitoring: {
    candidates: '/api/v1/monitoring/:jobKey/candidates',
    candidateDetail: '/api/v1/monitoring/:jobKey/candidates/:amlId',
    hits: '/api/v1/monitoring/:jobKey/entities/:amlId/hits',
    hitDetail: '/api/v1/monitoring/:jobKey/entities/:amlId/hits/:hitAmlId',
    decision: '/api/v1/monitoring/:jobKey/entities/:amlId/hits/:hitAmlId',
    stats: '/api/v1/monitoring/:jobKey/stats',
  },

  // Profile Management
  profiles: {
    list: '/api/v1/profiles',
    create: '/api/v1/profiles',
    detail: '/api/v1/profiles/:profileKey',
    update: '/api/v1/profiles/:profileKey',
    delete: '/api/v1/profiles/:profileKey',
  },

  // Search Operations
  search: {
    // ✅ CORRECT: AML-Guard uses single unified endpoint for all entity types
    entity: '/api/v1/search',
    batch: '/api/v1/search/batch',
    relation: '/api/v1/search/relation',
    history: '/api/v1/search/history',
  },
}

// Available Search Profiles (from demo credentials)
export const SEARCH_PROFILES = [
  'all_generic_hard',
  'eur_generic_hard',
  'all_lexisnexis',
  'all_generic_medium_EU-R',
  'all_panorama_1',
] as const

export type SearchProfile = (typeof SEARCH_PROFILES)[number]

// Application Configuration
export const APP_CONFIG = {
  name: import.meta.env.VITE_APP_NAME || 'AML-Guard',
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  environment: import.meta.env.VITE_ENV || 'development',
}
